import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiList, FiLink, FiCheck, FiChevronRight, FiArrowRight } from "react-icons/fi";
import { FaWhatsapp, FaFacebookF, FaLinkedinIn, FaTwitter } from "react-icons/fa";
import blogs from "../../data/blogs";
import { getReadingTime, slugify } from "../../utils/reading";
import { categoryStyle, categoryColor } from "../../utils/category";
import { heroCropStyle } from "../../utils/image";
import { SITE_URL } from "../../config";
import "./BlogDetail/BlogDetail.css";

const BlogArticle = ({ post }) => {
  const [copied, setCopied] = useState(false);

  const content = post.content || "";
  const tags = Array.isArray(post.tags)
    ? post.tags
    : (post.tags || "").split(",").map((t) => t.trim()).filter(Boolean);

  const { html, toc } = useMemo(() => {
    if (typeof window === "undefined" || typeof DOMParser === "undefined") {
      return { html: content, toc: [] };
    }
    const doc = new DOMParser().parseFromString(content, "text/html");
    const items = [...doc.querySelectorAll("h2")].map((heading, index) => {
      const id = `${slugify(heading.textContent) || "bolum"}-${index}`;
      heading.setAttribute("id", id);
      return { id, text: heading.textContent };
    });
    return { html: doc.body.innerHTML, toc: items };
  }, [content]);

  const authorName = post.author || "Lingify";
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("tr-TR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
  const readingTime = getReadingTime(content);

  const relatedBlogs = blogs
    .filter((b) => b.slug !== post.slug && b.category === post.category)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  const shareUrl = `${SITE_URL}/blog/${post.slug}`;
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${post.title} ${shareUrl}`)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="blog-detail-page">
      <nav className="blog-breadcrumb" aria-label="breadcrumb">
        <div className="blog-breadcrumb-inner">
          <Link to="/">Ana Sayfa</Link>
          <FiChevronRight />
          <Link to="/blog">Blog</Link>
          <FiChevronRight />
          <span className="current">{post.title}</span>
        </div>
      </nav>

      <header className={`blog-hero2 ${post.image ? "has-image" : "no-image"}`}>
        {post.image && (
          <div className="blog-hero2-media">
            <img
              className="blog-hero2-bg"
              src={post.image}
              alt={post.title}
              style={heroCropStyle(post.imageCrop)}
            />
            <div className="blog-hero2-overlay" />
          </div>
        )}
        <div className="blog-hero2-content">
          <span className="blog-hero2-badge" style={categoryStyle(post.category)}>
            {post.category}
          </span>
          <h1>{post.title || "Başlık"}</h1>
          <div className="blog-hero2-foot">
            <span className="blog-avatar large">
              <img src="/shortlogoAphasiAI.svg" alt="Lingify" />
            </span>
            <div className="blog-hero2-author">
              <span className="name">{authorName}</span>
              <span className="meta">{formattedDate} · {readingTime} okuma</span>
            </div>
          </div>
        </div>
      </header>

      <div className="blog-body">
        <article className="blog-detail-container">
          <div className="blog-detail-content" dangerouslySetInnerHTML={{ __html: html }} />

          <div className="blog-share">
            <span className="blog-share-label">Bu yazıyı paylaş</span>
            <div className="blog-share-buttons">
              <a className="share-btn share-whatsapp" href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp'ta paylaş"><FaWhatsapp /></a>
              <a className="share-btn share-twitter" href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" aria-label="X'te paylaş"><FaTwitter /></a>
              <a className="share-btn share-facebook" href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook'ta paylaş"><FaFacebookF /></a>
              <a className="share-btn share-linkedin" href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn'de paylaş"><FaLinkedinIn /></a>
              <button type="button" className="share-btn share-copy" onClick={handleCopy} aria-label="Bağlantıyı kopyala">
                {copied ? <FiCheck /> : <FiLink />}
              </button>
            </div>
          </div>
        </article>

        <aside className="blog-sidebar">
          <div className="sidebar-card author-card">
            <span className="blog-avatar large">
              <img src="/shortlogoAphasiAI.svg" alt="Lingify" />
            </span>
            <div className="author-card-info">
              <span className="author-name">{authorName}</span>
              <span className="author-role">
                {post.editor ? `Editör: ${post.editor}` : "Lingify"}
              </span>
            </div>
          </div>

          {toc.length > 1 && (
            <div className="sidebar-card">
              <p className="sidebar-title"><FiList /> İçindekiler</p>
              <nav className="sidebar-toc">
                <ul>
                  {toc.map((item) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} onClick={(e) => handleTocClick(e, item.id)}>
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          )}

          {relatedBlogs.length > 0 && (
            <div className="sidebar-card">
              <p className="sidebar-title">İlgili Yazılar</p>
              <div className="sidebar-related">
                {relatedBlogs.map((related) => (
                  <Link to={`/blog/${related.slug}`} key={related.id} className="sidebar-related-item">
                    <img src={related.image} alt={related.title} loading="lazy" />
                    <div className="sidebar-related-info">
                      <span className="cat" style={{ color: categoryColor(related.category) }}>
                        {related.category}
                      </span>
                      <span className="ttl">{related.title}</span>
                    </div>
                  </Link>
                ))}
              </div>
              <Link to="/blog" className="sidebar-all">
                Tümünü Gör <FiArrowRight />
              </Link>
            </div>
          )}

          <div className="sidebar-cta">
            <span className="cta-eyebrow">Topluluk</span>
            <h3>Afazi topluluğumuza katılın</h3>
            <p>Etkinliklerden haberdar olun, vaka sohbetlerine katılın ve destek ağımızın parçası olun.</p>
            <Link to="/brodmann" className="cta-btn">
              Topluluğa Katıl <FiArrowRight />
            </Link>
          </div>

          {tags.length > 0 && (
            <div className="sidebar-card">
              <p className="sidebar-title">Etiketler</p>
              <div className="sidebar-tags">
                {tags.map((tag) => (
                  <span key={tag} className="sidebar-tag">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default BlogArticle;
